import React, { useState, useEffect, useRef } from 'react';
import { BottomAppShowcase, PageShell } from './public/PublicPrimitives';
import Logo, { CleroS, CleroWordmarkSvg } from './Logo';
import {
  ShieldAlert,
  AlertCircle,
  Clock3,
  Landmark,
  XCircle,
  Mail,
  Check,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Lock,
  MessageSquare,
  HelpCircle,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { FaApple, FaAndroid } from 'react-icons/fa';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import Lenis from 'lenis';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../constants/downloadLinks';

export { ANDROID_INVITE_URL, IOS_INVITE_URL };

interface SupportTopic {
  key: string;
  label: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
}

const SUPPORT_TOPICS: SupportTopic[] = [
  {
    key: 'report_user',
    label: 'Report a user or content',
    subtitle: 'Report fraudulent, abusive, or suspicious activity',
    icon: ShieldAlert,
  },
  {
    key: 'identity_verification',
    label: 'Identity verification',
    subtitle: 'Help with verification, document upload, or status',
    icon: ShieldAlert,
  },
  {
    key: 'payments',
    label: 'Payments',
    subtitle: 'Issues with sending, receiving, or pending transfers',
    icon: AlertCircle,
  },
  {
    key: 'requests',
    label: 'Requests',
    subtitle: 'Problems with requesting money or payment links',
    icon: Clock3,
  },
  {
    key: 'account_bank',
    label: 'Account & bank',
    subtitle: 'Manage bank links, account settings, or security',
    icon: Landmark,
  },
  {
    key: 'not_received',
    label: 'I didn’t receive money',
    subtitle: 'Verify status of incoming transfer or request',
    icon: AlertCircle,
  },
  {
    key: 'wrong_person',
    label: 'Sent payment by mistake',
    subtitle: 'Cancel or dispute a payment made in error',
    icon: XCircle,
  },
  {
    key: 'pending',
    label: 'Payment is pending',
    subtitle: 'Check why a transaction is taking longer to process',
    icon: Clock3,
  },
  {
    key: 'something_else',
    label: 'Something else',
    subtitle: 'Other questions or general support',
    icon: MessageSquare,
  },
];

const FAQS = [
  {
    category: 'Payments & Direct Transfers',
    items: [
      {
        q: 'Why is my payment pending or taking time?',
        a: 'Most Clero bank-to-bank transfers clear on the same day* when submitted on banking business days before 2:00 PM EST. Transfers submitted after 2:00 PM EST, on weekends, or during federal banking holidays settle on the next business day (e.g. transfers initiated Friday afternoon or over the weekend settle on Monday). Bank holidays or first-time bank security checks can occasionally extend settlement to 1–2 business days.',
      },
      {
        q: 'How fast do direct bank transfers settle?',
        a: 'Standard bank-to-bank transfers in Clero settle on the same day* when initiated before 2:00 PM EST on banking business days. Transfers initiated after 2:00 PM EST or over weekends/bank holidays settle on the next business day. Unlike older wallet platforms, funds land directly in your connected checking account with $0 instant fees.',
      },
      {
        q: 'Do my friends need to download Clero to pay or claim money?',
        a: 'No! When you create a split, Clero sends an instant SMS link. Your friends can pay or receive funds directly via web link with their bank, without having to download the app.',
      },
      {
        q: 'Are there any hidden transfer or cashout fees?',
        a: 'No. Clero operates on direct ACH banking rails. Transfers between checking accounts have $0 cashout fees and $0 deposit hold penalties.',
      },
    ],
  },
  {
    category: 'Bank Connections & Security',
    items: [
      {
        q: 'How does Clero connect to my bank?',
        a: 'Clero partners with Plaid Inc. and Astra Financial to connect directly with over 12,000 US financial institutions. We use bank-grade 256-bit encryption and never store your banking username or password.',
      },
      {
        q: 'Can Clero see my full credit card or bank account number?',
        a: 'No. Clero only receives encrypted tokens and masked account identifiers (e.g. "Checking ...4829") from Plaid. We never store raw card numbers or routing numbers.',
      },
      {
        q: 'Is my data used to train AI models?',
        a: 'Never. Clero uses Google Gemini APIs via Vertex AI strictly on-demand for receipt itemization. Your receipt photos, emails, and financial data are never retained to train foundation AI models.',
      },
    ],
  },
  {
    category: 'Receipt Scanning & Gmail Sync',
    items: [
      {
        q: 'What email access does Clero request?',
        a: 'If you connect Gmail, Clero requests read-only access strictly to detect likely digital receipts, DoorDash/Uber invoices, and ride summaries. We never send emails, modify messages, or share mailbox data with third parties.',
      },
      {
        q: 'How do I disconnect Gmail or delete my receipt data?',
        a: 'You can disconnect your Google account anytime in the Clero mobile app Settings. Synced receipt suggestions expire after 30 days and are purged during daily cleanup, or removed immediately upon disconnect.',
      },
    ],
  },
];

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const isValidEmailAddress = (val: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val.trim());
};

const isValidPhoneNumber = (val: string): boolean => {
  if (!val.trim()) return true;
  const digits = val.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const SupportPage: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<SupportTopic | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<string | null>(null);

  const formRef = useRef<HTMLDivElement | null>(null);
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.85,
    });
    lenisRef.current = lenis;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    lenis.on('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    const urlParams = new URLSearchParams(window.location.search);
    const initialTopicKey = urlParams.get('topic');
    if (initialTopicKey) {
      const matched = SUPPORT_TOPICS.find((t) => t.key === initialTopicKey);
      if (matched) setSelectedTopic(matched);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  };

  const handleSelectTopic = (topic: SupportTopic) => {
    setSelectedTopic(topic);
    setErrorMessage(null);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setEmail(val);
    if (emailError && isValidEmailAddress(val)) {
      setEmailError(null);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    if (phoneError && isValidPhoneNumber(formatted)) {
      setPhoneError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    let hasError = false;
    if (!email.trim()) {
      setEmailError('Please enter your contact email address.');
      hasError = true;
    } else if (!isValidEmailAddress(email)) {
      setEmailError('Please enter a valid email address (e.g. name@example.com).');
      hasError = true;
    } else {
      setEmailError(null);
    }

    if (phone.trim() && !isValidPhoneNumber(phone)) {
      setPhoneError('Please enter a valid 10-digit US phone number.');
      hasError = true;
    } else {
      setPhoneError(null);
    }

    if (!message.trim()) {
      setErrorMessage('Please describe the issue or what happened.');
      hasError = true;
    }

    if (hasError) return;

    setSubmitting(true);
    const generatedTicketId = `CLR-SUP-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

    try {
      const submitFeedback = httpsCallable(functions, 'submitUserFeedback');

      const res: any = await submitFeedback({
        type: 'support',
        topic: selectedTopic?.label || 'General Support',
        referenceId: referenceId.trim() || null,
        message: message.trim(),
        senderEmail: email.trim(),
        senderPhone: phone.trim() || null,
        ticketId: generatedTicketId,
      });

      const confirmedTicketId = res?.data?.ticketId || generatedTicketId;
      setSubmittedTicketId(confirmedTicketId);
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err?.message || 'Failed to send request. Please try again or email support@clero.money.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell className="overflow-x-hidden bg-[#fafaf7]">
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled ? 'pt-3.5 px-4 sm:px-6' : 'pt-0 px-0'
        }`}
      >
        <header
          className={`pointer-events-auto w-full transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isScrolled
              ? 'max-w-[480px] rounded-[999px] bg-white/85 backdrop-blur-2xl border border-slate-200/80 px-4 sm:px-5 py-2 shadow-sm'
              : 'max-w-[720px] sm:max-w-[760px] rounded-[0px] bg-transparent backdrop-blur-none border-b border-slate-200/50 px-4 sm:px-6 py-3.5'
          }`}
        >
          <div className="flex items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                className="flex items-center transition opacity-95 hover:opacity-100"
                aria-label="Clero Home"
              >
                <CleroWordmarkSvg height={24} color="#0D9488" />
              </a>
            </div>

            <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
              <button
                type="button"
                onClick={() => navigate('/support')}
                className="text-xs sm:text-sm font-bold text-teal-800 hover:text-teal-950 transition-colors whitespace-nowrap px-1 py-1"
              >
                Help & Support
              </button>

              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('bottom-download');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center justify-center rounded-full bg-[var(--clero-primary)] px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold text-white transition-all hover:bg-[var(--clero-primary-dark)] shadow-sm active:scale-95 whitespace-nowrap"
              >
                Download App
              </button>
            </div>
          </div>
        </header>
      </div>

      <div className="h-16 sm:h-20" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-10 sm:pb-14 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl text-center space-y-3.5">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-950">
            How can we help you today?
          </h1>
          <p className="mx-auto max-w-xl text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
            We're here to help. Select a topic below and our team will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* MAIN CONTENT AREA */}
      <section className="py-4 sm:py-6 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl space-y-10 sm:space-y-14">

          {/* TOPICS SELECTOR */}
          <div>
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <div>
                <h2 className="text-lg sm:text-2xl font-black text-slate-950 tracking-tight">
                  Topics
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">
                  Select a topic to start your support request
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
              {SUPPORT_TOPICS.map((topic) => {
                const Icon = topic.icon;
                const isSelected = selectedTopic?.key === topic.key;
                return (
                  <button
                    key={topic.key}
                    type="button"
                    onClick={() => handleSelectTopic(topic)}
                    className={`group relative text-left rounded-2xl sm:rounded-[22px] p-3.5 sm:p-5 transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                      isSelected
                        ? 'bg-teal-900 text-white shadow-md shadow-teal-950/15 ring-2 ring-teal-700'
                        : 'bg-white text-slate-950 border border-slate-200/80 hover:border-teal-600/40 hover:bg-slate-50/50 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-sm'
                    }`}
                  >
                    <div>
                      {/* Top Row: Icon Container & Selection Badge */}
                      <div className="flex items-center justify-between mb-2.5 sm:mb-3">
                        <div
                          className={`flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-xl transition-colors ${
                            isSelected ? 'bg-white/15 text-white' : 'bg-teal-50 text-teal-800 group-hover:bg-teal-100'
                          }`}
                        >
                          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        {isSelected ? (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-400 text-slate-950">
                            <Check className="h-3 w-3 stroke-[3]" />
                          </div>
                        ) : (
                          <div className="h-4 w-4 rounded-full border border-slate-200 group-hover:border-slate-300 transition-colors" />
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="font-extrabold text-xs sm:text-[15px] tracking-tight leading-snug">
                        {topic.label}
                      </h3>

                      {/* Subtitle */}
                      <p
                        className={`mt-1 text-[11px] sm:text-xs font-medium leading-relaxed line-clamp-2 ${
                          isSelected ? 'text-teal-100' : 'text-slate-500'
                        }`}
                      >
                        {topic.subtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SUPPORT FORM */}
          <div ref={formRef} className="rounded-3xl sm:rounded-[32px] bg-white p-6 sm:p-10 lg:p-12 border border-slate-200/80 shadow-sm">
            {submittedTicketId ? (
              <div className="text-center py-8 sm:py-10 space-y-5 max-w-md mx-auto animate-in fade-in zoom-in-95 duration-300">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <div className="space-y-2">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-teal-700 block">
                    Support Request Submitted
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                    We’re on it!
                  </h3>
                  <p className="text-sm font-medium text-slate-600 leading-relaxed">
                    Your request has been logged under ticket reference:
                  </p>
                  <div className="inline-block rounded-2xl bg-slate-100 px-4 py-2 text-xs font-mono font-bold text-slate-900 border border-slate-200">
                    {submittedTicketId}
                  </div>
                  <p className="text-xs font-medium text-slate-500 leading-relaxed pt-2">
                    A representative from the <strong className="text-slate-800">Clero team</strong> will review your details and respond via email within 24 hours.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSubmittedTicketId(null);
                    setMessage('');
                    setSelectedTopic(null);
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-6 py-3 text-xs font-bold text-white hover:bg-slate-800 transition-all cursor-pointer"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span>Submit Another Request</span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-xl sm:text-2xl font-black text-slate-950 tracking-tight">
                      {selectedTopic ? `Help with: ${selectedTopic.label}` : 'Submit a Support Request'}
                    </h3>
                    {selectedTopic && (
                      <button
                        type="button"
                        onClick={() => setSelectedTopic(null)}
                        className="text-xs font-bold text-teal-700 hover:text-teal-900 transition-colors"
                      >
                        Change topic
                      </button>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-slate-500 mt-1">
                    Please provide your contact information and details below.
                  </p>
                </div>

                {errorMessage && (
                  <div className="rounded-2xl bg-red-50 border border-red-200/80 p-4 text-xs sm:text-sm font-semibold text-red-700">
                    {errorMessage}
                  </div>
                )}

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label
                      htmlFor="support-email"
                      className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                    >
                      Email <span className="text-teal-700">*</span>
                    </label>
                    <input
                      id="support-email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="you@example.com"
                      className={`w-full rounded-xl bg-slate-50 border border-slate-200/80 px-4 py-3 text-sm font-medium text-slate-950 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-all ${
                        emailError ? 'border-red-500 ring-2 ring-red-500/20 bg-red-50/30' : ''
                      }`}
                    />
                    {emailError ? (
                      <p className="text-[11px] text-red-600 font-bold">{emailError}</p>
                    ) : (
                      <p className="text-[11px] text-slate-400 font-medium">
                        We'll use this email to respond to your request.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label
                      htmlFor="support-phone"
                      className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                    >
                      Phone <span className="text-slate-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      id="support-phone"
                      type="tel"
                      value={phone}
                      onChange={handlePhoneChange}
                      placeholder="(555) 000-0000"
                      className={`w-full rounded-xl bg-slate-50 border border-slate-200/80 px-4 py-3 text-sm font-medium text-slate-950 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-all ${
                        phoneError ? 'border-red-500 ring-2 ring-red-500/20 bg-red-50/30' : ''
                      }`}
                    />
                    {phoneError && <p className="text-[11px] text-red-600 font-bold">{phoneError}</p>}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="support-reference"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                  >
                    Payment Reference ID <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    id="support-reference"
                    type="text"
                    value={referenceId}
                    onChange={(e) => setReferenceId(e.target.value)}
                    placeholder="e.g. CLR-89X2A"
                    className="w-full rounded-xl bg-slate-50 border border-slate-200/80 px-4 py-3 text-sm font-medium text-slate-950 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="support-message"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700"
                  >
                    Tell us what went wrong <span className="text-teal-700">*</span>
                  </label>
                  <textarea
                    id="support-message"
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe what happened..."
                    className="w-full rounded-xl bg-slate-50 border border-slate-200/80 p-4 text-sm font-medium text-slate-950 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-all leading-relaxed resize-none"
                  />
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                    <Lock className="h-3.5 w-3.5 text-slate-400" />
                    <span>Confidential & Encrypted Direct Banking Support</span>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[var(--clero-primary)] hover:bg-[var(--clero-primary-dark)] px-8 py-3.5 text-xs sm:text-sm font-bold text-white shadow-sm disabled:opacity-50 transition-all cursor-pointer active:scale-98"
                  >
                    {submitting ? 'Submitting...' : 'Submit request'}
                    {!submitting && <ArrowRight className="h-4 w-4" />}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="rounded-3xl bg-white p-8 sm:p-12">
            <div className="border-b border-slate-100 pb-6 mb-8">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-8">
              {FAQS.map((categoryGroup, catIdx) => (
                <div key={categoryGroup.category} className="space-y-4">
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">
                    {categoryGroup.category}
                  </h3>
                  <div className="divide-y divide-slate-100">
                    {categoryGroup.items.map((faq, itemIdx) => {
                      const faqKey = `${catIdx}-${itemIdx}`;
                      const isOpen = openFaqIndex === faqKey;
                      return (
                        <div key={faq.q} className="py-4">
                          <button
                            type="button"
                            onClick={() => setOpenFaqIndex(isOpen ? null : faqKey)}
                            className="flex w-full items-start justify-between gap-4 text-left font-bold text-sm sm:text-base text-slate-950 hover:text-teal-900 transition-colors"
                          >
                            <span>{faq.q}</span>
                            <ChevronDown
                              className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 mt-1 ${
                                isOpen ? 'rotate-180 text-teal-600' : ''
                              }`}
                            />
                          </button>
                          {isOpen && (
                            <p className="mt-2.5 text-xs sm:text-sm text-slate-600 font-medium leading-relaxed">
                              {faq.a}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mt-12 mx-auto max-w-[var(--clero-max-content)] px-4 sm:px-6 lg:px-8 mb-12">
        <BottomAppShowcase />
      </div>

      <footer className="border-t border-slate-200/80 bg-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[var(--clero-max-content)]">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              <img src="/clero_icon.png" alt="Clero" className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl shadow-sm object-cover" />
              <p className="text-xs font-semibold text-slate-400">
                Secured with direct bank connections via Plaid & Astra Financial.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Compare</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare')} type="button">All Splitting Apps</button></li>
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare/splitwise')} type="button">Clero vs Splitwise</button></li>
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare/venmo')} type="button">Clero vs Venmo</button></li>
                <li><button className="transition hover:text-teal-700" onClick={() => navigate('/compare/zelle')} type="button">Clero vs Zelle</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Product</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/')} type="button">Features</button></li>
                <li><a className="transition hover:text-slate-950" href={IOS_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for iOS</a></li>
                <li><a className="transition hover:text-slate-950" href={ANDROID_INVITE_URL} target="_blank" rel="noopener noreferrer">Download for Android</a></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Resources</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/blog')} type="button">Blog</button></li>
                <li><button className="text-teal-700 font-bold" onClick={() => navigate('/support')} type="button">Help & Support</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/compare')} type="button">Compare Apps</button></li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-900">Legal & Privacy</p>
              <ul className="mt-3 space-y-2 text-xs font-semibold text-slate-600">
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/privacy')} type="button">Privacy Policy</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/terms')} type="button">Terms of Service</button></li>
                <li><button className="transition hover:text-slate-950" onClick={() => navigate('/data-deletion')} type="button">Data Deletion</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-slate-100 pt-6 space-y-3 text-center text-xs text-slate-400">
            <p className="max-w-3xl mx-auto leading-relaxed text-slate-400/90">
              * Same-day bank transfers are processed via Same Day ACH on banking business days (Monday–Friday, excluding federal holidays). Transfers initiated before 2:00 PM EST on business days typically settle the same day. Transfers initiated after 2:00 PM EST, on weekends, or on federal banking holidays settle on the next business day (e.g. transfers initiated on Friday afternoon or over the weekend settle on Monday). Settlement timing may vary depending on your bank.
            </p>
            <p>© 2026 Oryn Technologies Inc. All rights reserved. Clero is a financial technology platform, not a bank.</p>
          </div>
        </div>
      </footer>
    </PageShell>
  );
};

export default SupportPage;
